import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FaClock, FaTimesCircle } from "react-icons/fa";
import { Menu, X } from "lucide-react";
import mainlogo from "../assets/logo.webp";
import "./Header.css";
import TempleStatusPopup from "./TempleStatusPopup";
/* =========================================================
   CONSTANTS
========================================================= */
const timings = [
  { time: "04:10", label: "Samadhi Aarti" },
  { time: "04:30", label: "Mangal Aarti" },
  { time: "05:30", label: "Japa Class" },
  { time: "07:25", label: "Narshima Aarti" },
  { time: "07:30", label: "Darshan Aarti" },
  { time: "08:00", label: "Bhagvatam Class" },
  { time: "12:15", label: "Raj Bhoga" },
  { time: "16:00", label: "Dhupa Aarti" },
  { time: "18:45", label: "Tulsi Aarti" },
  { time: "21:00", label: "Shayana Aarti" },
];
/* =========================================================
   HELPERS
========================================================= */
const timeToMinutes = (time) => {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
};
const getTempleStatus = () => {
  const now = new Date();
  const currentMinutes = now.getHours() * 60 + now.getMinutes();
  const closedPeriods = [
    {
      start: timeToMinutes("13:00"),
      end: timeToMinutes("16:00"),
    },
    {
      start: timeToMinutes("21:15"),
      end: 24 * 60,
    },
    {
      start: 0,
      end: timeToMinutes("04:10"),
    },
  ];
  const isClosed = closedPeriods.some(
    ({ start, end }) => currentMinutes >= start && currentMinutes < end,
  );
  const upcoming =
    timings.find(({ time }) => timeToMinutes(time) > currentMinutes) ||
    timings[0];
  return {
    status: isClosed ? "Closed" : "Open",
    upcoming,
  };
};
/* =========================================================
   HEADER
========================================================= */
const Header = () => {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [templeStatus, setTempleStatus] = useState({
    status: "",
    upcoming: null,
  });
  const modalRef = useRef(null);
  /* =======================================================
     TEMPLE STATUS
  ======================================================= */
  useEffect(() => {
    const updateStatus = () => {
      setTempleStatus(getTempleStatus());
    };
    updateStatus();
    const interval = setInterval(updateStatus, 60000);
    return () => clearInterval(interval);
  }, []);
  /* =======================================================
     MOBILE MENU
  ======================================================= */
  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  };
  const toggleMobileMenu = () => {
    setMobileMenuOpen((previous) => !previous);
    setActiveDropdown(null);
  };
  /* =======================================================
     DROPDOWN
  ======================================================= */
  const toggleDropdown = (name) => {
    setActiveDropdown((previous) => (previous === name ? null : name));
  };
  /* =======================================================
     SCHEDULE
  ======================================================= */
  const openSchedule = () => {
    closeMobileMenu();
    setIsScheduleOpen(true);
  };
  const closeSchedule = () => {
    setIsScheduleOpen(false);
  };
  /* =======================================================
     ESC KEY
  ======================================================= */
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key !== "Escape") {
        return;
      }
      closeMobileMenu();
      closeSchedule();
    };
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);
  /* =======================================================
     BODY SCROLL LOCK
  ======================================================= */
  useEffect(() => {
    if (mobileMenuOpen || isScheduleOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen, isScheduleOpen]);
  /* =======================================================
     ROUTE CHANGE
  ======================================================= */
  useEffect(() => {
    setMobileMenuOpen(false);
    setActiveDropdown(null);
  }, [location.pathname]);
  /* =======================================================
     ACTIVE ROUTE
  ======================================================= */
  const isActive = (path) => location.pathname === path;
  const isPrefixActive = (path) => location.pathname.startsWith(path);
  return (
    <header
      className={`iskcon-header ${
        location.pathname === "/" ? "iskcon-header-home" : "iskcon-header-inner"
      }`}
    >
      {" "}
      {/* =================================================
          HEADER BAR
      ================================================= */}
      <div className="iskcon-header-bar">
        {/* LOGO */}
        <Link
          to="/"
          className="iskcon-logo"
          aria-label="ISKCON Ujjain Home"
          onClick={closeMobileMenu}
        >
          <img
            src={mainlogo}
            alt="ISKCON Ujjain"
            className="iskcon-logo-image"
          />
        </Link>
        {/* =================================================
            DESKTOP NAVIGATION
        ================================================= */}
        <nav className="iskcon-desktop-nav" aria-label="Main navigation">
          <Link
            to="/"
            className={
              isActive("/") ? "iskcon-nav-link active" : "iskcon-nav-link"
            }
          >
            Home
          </Link>
          {/* ABOUT */}
          <div className="iskcon-nav-dropdown">
            <button
              type="button"
              className={
                isPrefixActive("/about")
                  ? "iskcon-nav-link iskcon-nav-button active"
                  : "iskcon-nav-link iskcon-nav-button"
              }
              onClick={() => toggleDropdown("desktop-about")}
              aria-expanded={activeDropdown === "desktop-about"}
            >
              About
              <span className="dropdown-arrow">▾</span>
            </button>
            {activeDropdown === "desktop-about" && (
              <div className="iskcon-dropdown-menu">
                <Link to="/about/iskconujjain">ISKCON Ujjain</Link>
                <Link to="/about/SrilaPrabhupada">Srila Prabhupada</Link>
                <Link to="/about/BcsSwami">HH Bhakti Charu Swami Maharaja</Link>
              </div>
            )}
          </div>
          <Link
            to="/calendar"
            className={
              isActive("/calendar")
                ? "iskcon-nav-link active"
                : "iskcon-nav-link"
            }
          >
            Vaishnava Calendar
          </Link>
          <Link
            to="/contact"
            className={
              isActive("/contact")
                ? "iskcon-nav-link active"
                : "iskcon-nav-link"
            }
          >
            Contact
          </Link>
          {/* MEDIA */}
          <div className="iskcon-nav-dropdown">
            <button
              type="button"
              className={
                isActive("/gallery") || isActive("/DailyDarshan")
                  ? "iskcon-nav-link iskcon-nav-button active"
                  : "iskcon-nav-link iskcon-nav-button"
              }
              onClick={() => toggleDropdown("desktop-media")}
              aria-expanded={activeDropdown === "desktop-media"}
            >
              Media
              <span className="dropdown-arrow">▾</span>
            </button>
            {activeDropdown === "desktop-media" && (
              <div className="iskcon-dropdown-menu">
                <Link to="/gallery">Videos</Link>
                <Link to="/DailyDarshan">Daily Darshan</Link>
              </div>
            )}
          </div>
          {/* SERVICES */}
          <div className="iskcon-nav-dropdown">
            <button
              type="button"
              className={
                isPrefixActive("/services")
                  ? "iskcon-nav-link iskcon-nav-button active"
                  : "iskcon-nav-link iskcon-nav-button"
              }
              onClick={() => toggleDropdown("desktop-services")}
              aria-expanded={activeDropdown === "desktop-services"}
            >
              Our Services
              <span className="dropdown-arrow">▾</span>
            </button>
            {activeDropdown === "desktop-services" && (
              <div className="iskcon-dropdown-menu iskcon-services-menu">
                <Link to="/services/goshala">Goshala</Link>
                <Link to="/avanti-school">Avanti School</Link>
                <a
                  href="https://aihe.iskconujjain.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  AIHE
                </a>
                <a
                  href="https://guesthouse.iskconujjain.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Guest House
                </a>
                <a
                  href="https://iskconujjain.com/mahadev"
                  rel="noopener noreferrer"
                >
                  84 Mahadevs
                </a>
                <a
                  href="https://www.divinesky.net/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  ISKCON Altars
                </a>
                <a
                  href="https://arogyaniketan.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Ayurvedic Clinic
                </a>
                <Link to="/services/deities">Deities Dress</Link>
                <Link to="/services/govindas">Govinda's Restaurant</Link>
              </div>
            )}
          </div>
          {/* SCHEDULE */}
          <button
            type="button"
            className="iskcon-schedule-btn"
            onClick={openSchedule}
          >
            Schedule
          </button>
          {/* DONATE */}
          <a
            href="https://iskconujjaindonation.com/"
            className="iskcon-donate-btn"
            target="_blank"
            rel="noopener noreferrer"
          >
            Donate
          </a>
        </nav>
        {/* =================================================
            MOBILE MENU BUTTON
        ================================================= */}
        <button
          type="button"
          className="iskcon-mobile-toggle"
          onClick={toggleMobileMenu}
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileMenuOpen}
          aria-controls="iskcon-mobile-menu"
        >
          {mobileMenuOpen ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>
      {/* =================================================
          MOBILE OVERLAY
      ================================================= */}
      {mobileMenuOpen && (
        <div
          className="iskcon-mobile-overlay"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}
      {/* =================================================
          MOBILE DRAWER
      ================================================= */}
      <nav
        id="iskcon-mobile-menu"
        className={
          mobileMenuOpen ? "iskcon-mobile-menu open" : "iskcon-mobile-menu"
        }
        aria-label="Mobile navigation"
      >
        <div className="mobile-menu-header">
          <span>ISKCON Ujjain</span>
          <button
            type="button"
            onClick={closeMobileMenu}
            aria-label="Close menu"
          >
            <X size={22} />
          </button>
        </div>
        <Link
          to="/"
          onClick={closeMobileMenu}
          className={
            isActive("/") ? "mobile-nav-link active" : "mobile-nav-link"
          }
        >
          Home
        </Link>
        {/* MOBILE ABOUT */}
        <div className="mobile-dropdown">
          <button
            type="button"
            className="mobile-nav-link mobile-dropdown-trigger"
            onClick={() => toggleDropdown("mobile-about")}
            aria-expanded={activeDropdown === "mobile-about"}
          >
            <span>About</span>
            <span>{activeDropdown === "mobile-about" ? "−" : "+"}</span>
          </button>
          {activeDropdown === "mobile-about" && (
            <div className="mobile-submenu">
              <Link to="/about/iskconujjain" onClick={closeMobileMenu}>
                ISKCON Ujjain
              </Link>
              <Link to="/about/SrilaPrabhupada" onClick={closeMobileMenu}>
                Srila Prabhupada
              </Link>
              <Link to="/about/BcsSwami" onClick={closeMobileMenu}>
                HH Bhakti Charu Swami Maharaja
              </Link>
            </div>
          )}
        </div>
        <Link
          to="/calendar"
          onClick={closeMobileMenu}
          className={
            isActive("/calendar") ? "mobile-nav-link active" : "mobile-nav-link"
          }
        >
          Vaishnava Calendar
        </Link>
        <Link
          to="/contact"
          onClick={closeMobileMenu}
          className={
            isActive("/contact") ? "mobile-nav-link active" : "mobile-nav-link"
          }
        >
          Contact
        </Link>
        {/* MOBILE MEDIA */}
        <div className="mobile-dropdown">
          <button
            type="button"
            className="mobile-nav-link mobile-dropdown-trigger"
            onClick={() => toggleDropdown("mobile-media")}
            aria-expanded={activeDropdown === "mobile-media"}
          >
            <span>Media</span>
            <span>{activeDropdown === "mobile-media" ? "−" : "+"}</span>
          </button>
          {activeDropdown === "mobile-media" && (
            <div className="mobile-submenu">
              <Link to="/gallery" onClick={closeMobileMenu}>
                Videos
              </Link>
              <Link to="/DailyDarshan" onClick={closeMobileMenu}>
                Daily Darshan
              </Link>
            </div>
          )}
        </div>
        {/* MOBILE SERVICES */}
        <div className="mobile-dropdown">
          <button
            type="button"
            className="mobile-nav-link mobile-dropdown-trigger"
            onClick={() => toggleDropdown("mobile-services")}
            aria-expanded={activeDropdown === "mobile-services"}
          >
            <span>Our Services</span>
            <span>{activeDropdown === "mobile-services" ? "−" : "+"}</span>
          </button>
          {activeDropdown === "mobile-services" && (
            <div className="mobile-submenu">
              <Link to="/services/goshala" onClick={closeMobileMenu}>
                Goshala
              </Link>
              <Link to="/avanti-school" onClick={closeMobileMenu}>
                Avanti School
              </Link>
              <a
                href="https://aihe.iskconujjain.com/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
              >
                AIHE
              </a>
              <a
                href="https://guesthouse.iskconujjain.com/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
              >
                Guest House
              </a>
              <a
                href="https://iskconujjain.com/mahadev"
                onClick={closeMobileMenu}
              >
                84 Mahadevs
              </a>
              <a
                href="https://www.divinesky.net/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
              >
                ISKCON Altars
              </a>
              <a
                href="https://arogyaniketan.com/"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
              >
                Ayurvedic Clinic
              </a>
              <Link to="/services/deities" onClick={closeMobileMenu}>
                Deities Dress
              </Link>
              <Link to="/services/govindas" onClick={closeMobileMenu}>
                Govinda's Restaurant
              </Link>
            </div>
          )}
        </div>
        {/* MOBILE SCHEDULE */}
        <button
          type="button"
          className="mobile-schedule-btn"
          onClick={openSchedule}
        >
          Schedule
        </button>
        {/* MOBILE DONATE */}
        <a
          href="https://iskconujjaindonation.com/"
          className="mobile-donate-btn"
          target="_blank"
          rel="noopener noreferrer"
          onClick={closeMobileMenu}
        >
          Donate
        </a>
      </nav>
      {/* =================================================
          TEMPLE STATUS
      ================================================= */}
      <TempleStatusPopup templeStatus={templeStatus} />
      {/* =================================================
          SCHEDULE MODAL
      ================================================= */}
      {isScheduleOpen && (
        <div
          className="iskcon-modal-overlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeSchedule();
            }
          }}
        >
          <div
            className="iskcon-schedule-modal"
            ref={modalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="schedule-title"
          >
            <div className="iskcon-modal-header">
              <h2 id="schedule-title">ISKCON Ujjain Temple Timings</h2>
              <button
                type="button"
                onClick={closeSchedule}
                className="iskcon-close-btn"
                aria-label="Close schedule"
              >
                <FaTimesCircle size={24} />
              </button>
            </div>
            <p className="iskcon-closed-time">
              Closed from 13:00 – 16:00 and 21:15 – 04:10
            </p>
            <div className="iskcon-timing-grid">
              {timings.map((item) => (
                <div
                  key={`${item.time}-${item.label}`}
                  className="iskcon-timing-item"
                >
                  <FaClock />
                  <span className="time">{item.time}</span>
                  <span className="label">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
export default Header;
